import styled from '@emotion/styled';
import { Button, Typography } from '@material-ui/core';
import { Pathname } from 'history';
import { ClassAttributes, createElement, MouseEvent, MouseEventHandler } from 'react';
import { FormattedMessage } from 'react-intl';

import { subText } from '../constant/color';
import { SPACING_REGULAR, SPACING_UNIT } from '../constant/spacing';
import { gridLayoutContainer, gridLayoutItem } from '../styleUtility/gridLayout';
import mq from '../styleUtility/mediaquery';

interface Props {
  error: Error;
  solveTo: string;
  onClickBackButton: (e: MouseEvent<HTMLElement>, solveTo: string) => void;
  className?: string;
  onClickInquiryButton: MouseEventHandler<HTMLElement>;
  pathname: Pathname;
}

const FatalErrorScreen = ({
  error,
  solveTo,
  onClickBackButton,
  className,
  onClickInquiryButton,
  pathname,
}: ClassAttributes<HTMLElement> & Props) => {
  const viewItemProperties =
    // Setting at 'root path' and 'other page'
    pathname === '/' || pathname === '/projects'
      ? {
          subMessage: `RootError`,
          backButtonHandler: () => location.reload(),
          backButtonText: 'root.for_fatal_error.reload',
        }
      : {
          subMessage: `${error.message}.tips`,
          backButtonHandler: (e: MouseEvent<HTMLElement>) => onClickBackButton(e, solveTo),
          backButtonText: 'root.for_fatal_error.back',
        };

  return (
    <Root className={className}>
      <Logo src="/assets/ohno.png" />
      <Message variant="h5">
        <FormattedMessage id={viewItemProperties.subMessage}>
          {(tips: string) =>
            // If '${error.message}.tips' can translated, don`t show intercom button
            tips !== viewItemProperties.subMessage ? (
              tips
            ) : (
              <FormattedMessage
                id="message.request_intercom.text"
                values={{
                  button: (
                    <InquiryButton variant="outlined" size="large" onClick={onClickInquiryButton}>
                      <FormattedMessage id="message.request_intercom.button" />
                    </InquiryButton>
                  ),
                }}
              />
            )
          }
        </FormattedMessage>
      </Message>
      <BackButton variant="contained" color="primary" onClick={viewItemProperties.backButtonHandler}>
        <FormattedMessage id={viewItemProperties.backButtonText} />
      </BackButton>
      <Title>{error.message && <FormattedMessage id={`Error detail : ${error.message}`} />}</Title>
    </Root>
  );
};

const Root = styled('div')(
  gridLayoutContainer({
    columns: ['auto'],
    rows: ['auto', 'auto', 'auto', 'auto'],
    areas: [['logo'], ['errorMessage'], ['button'], ['title']],
    alignContent: 'center',
    justifyItems: 'center',
    alignItems: 'flex-start',
  }),
  {
    marginTop: `-${SPACING_REGULAR}px`,
    marginRight: 'auto',
    marginLeft: 'auto',
  },
);

const Logo = styled('img')(gridLayoutItem('logo'), {
  width: 305,
  marginTop: SPACING_UNIT * 8,
  marginBottom: SPACING_UNIT * 8,
});

const Title = styled(Typography)(gridLayoutItem('title'), {
  marginTop: SPACING_UNIT * 4,
  marginBottom: SPACING_UNIT * 4,
  fontWeight: 300,
  maxWidth: '90%',
  color: subText,
  fontSize: 14,
});

const Message = styled(Typography)(gridLayoutItem('errorMessage'), {
  marginBottom: SPACING_UNIT,
  maxWidth: 720,
  color: subText,
  [mq('sp')]: {
    maxWidth: '90%',
    fontSize: 16,
  },
});

const InquiryButton = styled(Button)({
  display: 'block !important',
  margin: `${SPACING_UNIT * 4}px auto 0 !important`,
  minWidth: '150px !important',
});

const BackButton = styled(Button)(gridLayoutItem('button'), {
  marginTop: `${SPACING_UNIT * 2}px !important`,
  minWidth: '150px !important',
});

export default FatalErrorScreen;
